import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { trackEvent } from "@/lib/audit";
import {
  COUNTRY_EXPERIENCE_REGISTRY,
  COUNTRY_HOME_PATH,
  type CountryCode,
} from "@/lib/country/countryExperienceEngine";
import {
  computeInvestmentRecommendation,
  detectBottleneck,
  getAllCountryMetrics,
  getInfluencerRanking,
  getViralitySummary,
  subscribe,
  type CountryMetrics,
} from "@/lib/marketDomination/marketScoreEngine";
import { listAlerts } from "@/lib/marketDomination/marketAlerts";

export const Route = createFileRoute("/admin/market-domination")({
  head: () => ({
    meta: [
      { title: "Market Domination · VerseSport War Room" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MarketDominationPage,
});

function MarketDominationPage() {
  const [, force] = useState(0);

  useEffect(() => {
    void trackEvent("market_domination_dashboard_opened", {});
    const off = subscribe(() => force((n) => n + 1));
    const t = window.setInterval(() => force((n) => n + 1), 30_000);
    return () => { off(); window.clearInterval(t); };
  }, []);

  const metrics = getAllCountryMetrics();
  const investment = useMemo(() => computeInvestmentRecommendation(10_000), [metrics]);
  const virality = getViralitySummary();
  const influencers = getInfluencerRanking(null, 10);
  const alerts = listAlerts().slice(0, 8);

  useEffect(() => {
    void trackEvent("market_budget_recommendation_viewed", { top: investment.top });
  }, [investment.top]);

  return (
    <main className="min-h-screen bg-background px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-5">
        <Header />
        <ExecutiveCards metrics={metrics} />
        <div className="grid gap-4 md:grid-cols-2">
          <CountryLeaderboard metrics={metrics} />
          <InvestmentPanel investment={investment} />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <AcquisitionPanel metrics={metrics} />
          <ViralityPanel data={virality} />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <RetentionPanel metrics={metrics} />
          <NationPridePanel metrics={metrics} />
        </div>
        <ConversionPanel metrics={metrics} />
        <InfluencerPanel rows={influencers} />
        <AlertsPanel alerts={alerts} />
        <p className="pb-4 text-[10px] text-muted-foreground">
          Datos derivados localmente del bus de eventos `vs:event`. Sin tablas nuevas, sin métricas falsas.
        </p>
      </div>
    </main>
  );
}

/* -------------------------------------------------------------------------- */

function Header() {
  return (
    <header className="space-y-1">
      <p className="text-[10px] font-black uppercase tracking-widest text-primary">War Room · M-00</p>
      <h1 className="text-2xl font-black tracking-tight">Centro de Comando Internacional</h1>
      <p className="text-sm text-muted-foreground">
        Brasil · México · Argentina · India. Refresh automático cada 30s.
      </p>
    </header>
  );
}

function trendBadge(t: CountryMetrics["trend"]): string {
  if (t === "up") return "🟢";
  if (t === "down") return "🔴";
  return "🟡";
}

function CountryLeaderboard({ metrics }: { metrics: CountryMetrics[] }) {
  return (
    <Panel id="M-01" title="Country Leaderboard">
      <ol className="space-y-2">
        {metrics.map((m, i) => {
          const exp = COUNTRY_EXPERIENCE_REGISTRY[m.country];
          return (
            <li
              key={m.country}
              className="flex items-center justify-between rounded-xl border p-2.5"
              style={{ borderColor: `${exp.accent_colors.primary}55` }}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-black tabular-nums">#{i + 1}</span>
                <span className="text-base">{exp.flag}</span>
                <span className="text-sm font-bold">{exp.country_name}</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="font-black tabular-nums">{m.score}</span>
                <span title="24h" className="tabular-nums">{m.growth.d24 > 0 ? "+" : ""}{m.growth.d24}%</span>
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  72h {m.growth.d72 > 0 ? "+" : ""}{m.growth.d72}% · 7d {m.growth.d7 > 0 ? "+" : ""}{m.growth.d7}%
                </span>
                <span>{trendBadge(m.trend)}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}

function InvestmentPanel({ investment }: { investment: ReturnType<typeof computeInvestmentRecommendation> }) {
  return (
    <Panel id="M-08" title="Budget Allocation Engine">
      <p className="mb-2 text-sm font-semibold">{investment.copy}</p>
      <ol className="space-y-1.5">
        {investment.ranking.map((r, i) => {
          const exp = COUNTRY_EXPERIENCE_REGISTRY[r.country];
          return (
            <li key={r.country} className="flex items-center justify-between rounded-lg border p-2 text-sm">
              <span>#{i + 1} {exp.flag} {exp.country_name}</span>
              <span className="font-black tabular-nums">{r.investmentScore}</span>
            </li>
          );
        })}
      </ol>
      <p className="mt-2 text-[10px] text-muted-foreground">
        40% crecimiento · 30% viralidad · 20% retención · 10% conversión.
      </p>
    </Panel>
  );
}

function AcquisitionPanel({ metrics }: { metrics: CountryMetrics[] }) {
  return (
    <Panel id="M-02" title="Acquisition Engine">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-muted-foreground">
          <tr><th className="text-left">País</th><th>Org</th><th>Inf</th><th>Ref</th><th>WA</th><th>TG</th><th>Top</th></tr>
        </thead>
        <tbody>
          {metrics.map((m) => {
            const exp = COUNTRY_EXPERIENCE_REGISTRY[m.country];
            const entries = Object.entries(m.acquisition) as [string, number][];
            const top = entries.sort((a, b) => b[1] - a[1])[0];
            return (
              <tr key={m.country} className="border-t">
                <td className="py-1.5">{exp.flag} {m.country}</td>
                <td className="text-center tabular-nums">{m.acquisition.organic}</td>
                <td className="text-center tabular-nums">{m.acquisition.influencer}</td>
                <td className="text-center tabular-nums">{m.acquisition.referral}</td>
                <td className="text-center tabular-nums">{m.acquisition.whatsapp}</td>
                <td className="text-center tabular-nums">{m.acquisition.telegram}</td>
                <td className="text-center font-bold">{top?.[1] ? top[0] : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Panel>
  );
}

function ViralityPanel({ data }: { data: ReturnType<typeof getViralitySummary> }) {
  const leader = data[0];
  return (
    <Panel id="M-03" title="Virality Engine">
      {leader && (
        <p className="mb-2 text-xs">
          País más viral:{" "}
          <span className="font-black">
            {COUNTRY_EXPERIENCE_REGISTRY[leader.country].flag} {leader.country}
          </span>{" "}
          · k = <span className="font-black tabular-nums">{leader.kFactor}</span>
        </p>
      )}
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-muted-foreground">
          <tr><th className="text-left">País</th><th>Shares</th><th>Refs</th><th>Chall</th><th>k</th></tr>
        </thead>
        <tbody>
          {data.map((r) => (
            <tr key={r.country} className="border-t">
              <td className="py-1.5">{COUNTRY_EXPERIENCE_REGISTRY[r.country].flag} {r.country}</td>
              <td className="text-center tabular-nums">{r.shares}</td>
              <td className="text-center tabular-nums">{r.referrals}</td>
              <td className="text-center tabular-nums">{r.challenges}</td>
              <td className="text-center font-black tabular-nums">{r.kFactor}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function retentionDot(v: number): string {
  if (v < 15) return "🔴";
  if (v < 25) return "🟡";
  return "🟢";
}

function RetentionPanel({ metrics }: { metrics: CountryMetrics[] }) {
  return (
    <Panel id="M-04" title="Retention Engine">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-muted-foreground">
          <tr><th className="text-left">País</th><th>D0</th><th>D1</th><th>D3</th><th>D7</th></tr>
        </thead>
        <tbody>
          {metrics.map((m) => (
            <tr key={m.country} className="border-t">
              <td className="py-1.5">{COUNTRY_EXPERIENCE_REGISTRY[m.country].flag} {m.country}</td>
              <td className="text-center tabular-nums">{m.retention.d0}%</td>
              <td className="text-center tabular-nums">{m.retention.d1}%</td>
              <td className="text-center tabular-nums">{m.retention.d3}%</td>
              <td className="text-center font-black tabular-nums">{m.retention.d7}% {retentionDot(m.retention.d7)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function NationPridePanel({ metrics }: { metrics: CountryMetrics[] }) {
  const sorted = [...metrics].sort((a, b) => b.nationSelected7d - a.nationSelected7d);
  const top = sorted[0];
  return (
    <Panel id="M-06" title="Nation Wars Impact">
      {top && (
        <p className="mb-2 text-xs">
          Mayor orgullo nacional:{" "}
          <span className="font-black">{COUNTRY_EXPERIENCE_REGISTRY[top.country].flag} {top.country}</span>
        </p>
      )}
      <ul className="space-y-1 text-xs">
        {sorted.map((m) => (
          <li key={m.country} className="flex items-center justify-between rounded-lg border p-2">
            <span>{COUNTRY_EXPERIENCE_REGISTRY[m.country].flag} {m.country}</span>
            <span className="tabular-nums">
              nación <b>{m.nationSelected7d}</b> · shares <b>{m.shares7d}</b> · refs <b>{m.referrals7d}</b>
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function ConversionPanel({ metrics }: { metrics: CountryMetrics[] }) {
  return (
    <Panel id="M-07" title="Conversion Engine">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-muted-foreground">
          <tr>
            <th className="text-left">País</th><th>Visit</th><th>Reg</th><th>Nación</th><th>Pred</th>
            <th>Share</th><th>Ref</th><th>Return</th><th>Cuello</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((m) => {
            const f = m.funnel;
            const bn = detectBottleneck(m.country);
            return (
              <tr key={m.country} className="border-t">
                <td className="py-1.5">{COUNTRY_EXPERIENCE_REGISTRY[m.country].flag} {m.country}</td>
                <td className="text-center tabular-nums">{f.visit}</td>
                <td className="text-center tabular-nums">{f.register}</td>
                <td className="text-center tabular-nums">{f.nation}</td>
                <td className="text-center tabular-nums">{f.prediction}</td>
                <td className="text-center tabular-nums">{f.share}</td>
                <td className="text-center tabular-nums">{f.referral}</td>
                <td className="text-center tabular-nums">{f.return}</td>
                <td className="text-center font-bold">
                  {bn ? `${bn.step} −${bn.dropPct}%` : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Panel>
  );
}

function InfluencerPanel({ rows }: { rows: ReturnType<typeof getInfluencerRanking> }) {
  return (
    <Panel id="M-05" title="Influencer Command Center · Top 10">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-muted-foreground">
          <tr>
            <th className="text-left">#</th><th className="text-left">Influencer</th>
            <th>País</th><th>Views</th><th>Sign</th><th>Shares</th><th>Refs</th><th>Pred</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.slug} className="border-t">
              <td className="py-1.5 tabular-nums">{i + 1}</td>
              <td>{r.displayName}</td>
              <td className="text-center">{COUNTRY_EXPERIENCE_REGISTRY[r.country].flag}</td>
              <td className="text-center tabular-nums">{r.views}</td>
              <td className="text-center tabular-nums">{r.signups}</td>
              <td className="text-center tabular-nums">{r.shares}</td>
              <td className="text-center tabular-nums">{r.referrals}</td>
              <td className="text-center tabular-nums">{r.predictions}</td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={8} className="py-3 text-center text-muted-foreground">Sin actividad de influencers todavía.</td></tr>
          )}
        </tbody>
      </table>
    </Panel>
  );
}

function AlertsPanel({ alerts }: { alerts: ReturnType<typeof listAlerts> }) {
  return (
    <Panel id="M-12" title="Alertas automáticas">
      {alerts.length ? (
        <ul className="space-y-1 text-xs">
          {alerts.map((a) => (
            <li key={a.id} className="flex items-start justify-between gap-2 rounded-lg border p-2">
              <span>{a.message}</span>
              <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
                {new Date(a.at).toLocaleTimeString()}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">Sin alertas activas. Todo dentro de umbrales.</p>
      )}
    </Panel>
  );
}

function ExecutiveCards({ metrics }: { metrics: CountryMetrics[] }) {
  return (
    <section className="grid grid-cols-2 gap-2 md:grid-cols-4">
      {metrics.map((m) => {
        const exp = COUNTRY_EXPERIENCE_REGISTRY[m.country];
        return (
          <Link
            key={m.country}
            to={COUNTRY_HOME_PATH[m.country] as never}
            className="rounded-2xl border p-3 transition hover:bg-muted/50"
            style={{
              borderColor: `${exp.accent_colors.primary}66`,
              background: `linear-gradient(135deg, ${exp.accent_colors.primary}12, transparent)`,
            }}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-black">{exp.flag} {m.country}</span>
              <span className="text-lg font-black tabular-nums">{m.score}</span>
            </div>
            <div className="mt-1 grid grid-cols-2 gap-1 text-[10px] text-muted-foreground">
              <span>users {m.registered7d}</span>
              <span>active {m.active7d}</span>
              <span>shares {m.shares7d}</span>
              <span>refs {m.referrals7d}</span>
              <span>pred {m.predictions7d}</span>
              <span>D7 {m.retention.d7}%</span>
            </div>
          </Link>
        );
      })}
    </section>
  );
}

function Panel({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border bg-card p-3">
      <header className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-black tracking-tight">{title}</h2>
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{id}</span>
      </header>
      {children}
    </section>
  );
}
